import { AfterViewInit, Component, inject, input, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ChartConfiguration } from 'chart.js';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions, fullConfig } from '../../../helpers';
import { CameraHealthService } from '../../../service/camera-health.service';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { UserLoginService } from '../../../service/user-login.service';
import { Branch, CameraHealthItem, GetCameraTimeSeriesRequest } from '../../../types';
import { MAX_INT32 } from '../../../utils';
import { CellCameraHealthActionsComponent } from './cell-camera-health-actions/cell-camera-health-actions.component';
import { CellCameraHealthCameraStatusComponent } from './cell-camera-health-camera-status/cell-camera-health-camera-status.component';

@Component({
  selector: 'app-camera-health',
  templateUrl: './camera-health.component.html',
  styleUrl: './camera-health.component.scss',
  standalone: false,
})
export class CameraHealthComponent implements AfterViewInit {
  private cameraHealthService = inject(CameraHealthService);

  notifyService = inject(NotifyService);
  posthogService = inject(PosthogService);
  userService = inject(UserLoginService);

  private cameraTable = viewChild<AgGridAngular>('cameraGrid');

  public branchId = input.required<number | undefined>();
  public branchData = input<Branch>();

  isLoading = signal<boolean>(true);
  isLoadingTimeSeries = signal<boolean>(false);
  cameras = signal<CameraHealthItem[]>([]);

  barChartLegend = signal<boolean>(true);
  barChartData = signal<ChartConfiguration<'bar'>['data']>({
    labels: [],
    datasets: [{ data: [] }],
  });

  barChartOptions = signal<ChartConfiguration<'bar'>['options']>({
    responsive: true,
    // aspect ratio is width / height, I'm writing it as a fraction to be explicit
    aspectRatio: 4 / 1,
    scales: {
      x: {
        ticks: { autoSkip: false, minRotation: 90 },
      },
    },
  });

  public readonly tableDef: GridOptions<CameraHealthItem> = {
    ...defaultTableDefOptions(),

    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly tableColumns: ColDef<CameraHealthItem>[] = [
    {
      field: 'name',
      headerName: 'Rhombus Camera Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'provider_camera_id',
      headerName: 'Provider Camera ID',
      minWidth: 300,
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'provider_branch_id',
      headerName: 'Provider Branch ID',
      minWidth: 300,
      hide: true,
    },
    {
      field: 'location',
      headerName: 'Location',
      minWidth: 300,
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      maxWidth: 200,
      cellRenderer: CellCameraHealthCameraStatusComponent,
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressMiniFilter: true,
        valueFormatter: (data) => {
          return capitalize(data.value);
        },
      },
    },
    {
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      width: 70,
      maxWidth: 70,
      sortable: false,
      cellRenderer: CellCameraHealthActionsComponent,
    },
  ];

  ngAfterViewInit() {
    this.loadTimeSeries();
    this.loadCameras();
  }

  loadCameras() {
    this.isLoading.set(true);
    this.cameraHealthService
      .listCameras({
        page: 1,
        itemsPerPage: MAX_INT32,
        branch: this.branchId() ?? 0,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((d) => {
        this.cameras.set(d.items);
      });
  }

  loadTimeSeries() {
    const timeSeriesRequest: GetCameraTimeSeriesRequest = {
      branch: this.branchId() ?? 0,
    };

    this.isLoadingTimeSeries.set(true);
    this.cameraHealthService.getCameraHealthTimeSeries(timeSeriesRequest).subscribe(
      (data) => {
        this.isLoadingTimeSeries.set(false);
        this.barChartData.set({
          labels: data.data_points.map((dp) => DateTime.fromISO(dp.date).toFormat('yyyy-MM-dd')),
          datasets: [
            {
              data: data.data_points.map((dp) => dp.count),
              label: 'Disconnected',
              backgroundColor: fullConfig.theme.colors.red[500],
            },
          ],
        });
      },
      (error) => {
        console.error('Error loading time series:', tryGetErrorMessage(error));
        this.notifyService.notify('Something went wrong loading time series data.');
        this.isLoadingTimeSeries.set(false);
      },
    );
  }

  export() {
    this.posthogService.capture('start_camera_health_export');
    this.posthogService.capture('download_camera_health_export');

    this.cameraTable()?.api.exportDataAsCsv({
      fileName: `camera_health_export_${DateTime.now().toFormat('yyyy-MM-dd')}.csv`,
      columnKeys: this.tableColumns.map((col) => col.field).filter(Boolean),
    });
  }
}
